import React, { useState, useEffect, type ReactNode, useCallback, useRef } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { logger } from '@/lib/logger';
import { PostModalContext, type PostModalContextType } from './usePostModalContext';

/** Returns true if the pathname is a profile-based route */
const isProfileRoute = (pathname: string): boolean => pathname.startsWith('/profile');

interface PostModalProviderProps {
  children: ReactNode;
}

export const PostModalProvider: React.FC<PostModalProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ username?: string; postId?: string }>();
  
  const [isOpen, setIsOpen] = useState(false);
  const [postId, setPostId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isInPlaceMode, setIsInPlaceMode] = useState(false);
  const [shouldFocusComment, setShouldFocusComment] = useState(false);
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [autoExpandParentId, setAutoExpandParentId] = useState<string | null>(null);
  
  // Track if modal was opened in-place (no URL change) - ref for internal use
  const isInPlaceModeRef = useRef(false);
  
  // Guard to prevent URL watcher from reopening modal during close
  const isClosingRef = useRef(false);
  
  // Ref mirror of isOpen — used inside URL-watcher without triggering re-runs
  const isOpenRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  
  // Track the route where the modal was opened from (origin-aware closing)
  const originRouteRef = useRef<string | null>(null);
  
  // Track whether URL params were actually pushed (distinguishes back-button from in-place open)
  const urlParamsPushedRef = useRef(false);

  // Monitor URL changes and auto-open modal when pattern matches
  useEffect(() => {
    // Universal close guard — skip all branches while a close sequence is in flight
    if (isClosingRef.current) return;
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    const photoParam = searchParams.get('photo');
    
    // --- /square modal query-param patterns ---
    // Pattern: /square?modal=post&post=X&user=Y
    if (pathParts[0] === 'square' && searchParams.get('modal') === 'post') {
      // Skip if closing in progress or already in-place (internal nav from openModal already set state)
      if (isClosingRef.current || isInPlaceModeRef.current) return;
      
      const postFromUrl = searchParams.get('post');
      const userFromUrl = searchParams.get('user');
      const actionFromUrl = searchParams.get('action');
      if (postFromUrl && userFromUrl) {
        isInPlaceModeRef.current = true;
        setIsInPlaceMode(true);
        setUsername(userFromUrl);
        setPostId(postFromUrl);
        setIsOpen(true);
        setShouldFocusComment(actionFromUrl === 'comment');
        setTargetCommentId(null);
        setHighlightCommentId(null);
        setAutoExpandParentId(null);
        return;
      }
    }
    
    // /square with no modal param → close only if URL params were actually pushed (browser back)
    if (pathParts[0] === 'square' && !searchParams.get('modal') && isOpenRef.current && urlParamsPushedRef.current) {
      setIsOpen(false);
      setPostId(null);
      setUsername(null);
      setIsInPlaceMode(false);
      setShouldFocusComment(false);
      setTargetCommentId(null);
      setHighlightCommentId(null);
      setAutoExpandParentId(null);
      isInPlaceModeRef.current = false;
      urlParamsPushedRef.current = false;
      return;
    }
    
    // Skip profile route monitoring if in-place mode
    if (isInPlaceModeRef.current) return;
    
    // --- Profile route patterns ---
    // Pattern: ['profile', 'username', 'post', 'postId']
    // Exclude if photo parameter exists (those are photo modal routes)
    const isPostRoute = 
      pathParts.length === 4 && 
      pathParts[0] === 'profile' && 
      pathParts[2] === 'post' &&
      !photoParam;
    
    if (isPostRoute) {
      // Parse username and postId directly from URL to avoid race conditions
      const usernameFromUrl = pathParts[1];
      const postIdFromUrl = pathParts[3];
      
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PostModalContext] Opening modal from URL:', {
          pathname: location.pathname,
          username: usernameFromUrl,
          postId: postIdFromUrl,
          action
        });
      }
      
      if (usernameFromUrl && postIdFromUrl) {
        setUsername(usernameFromUrl);
        setPostId(postIdFromUrl);
        setIsOpen(true);
        setShouldFocusComment(action === 'comment');
        setTargetCommentId(null);
        setHighlightCommentId(null);
        setAutoExpandParentId(null);
        return;
      }
    }
    
    // Close modal if not on post route
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PostModalContext] Closing modal - not on post route:', {
        pathname: location.pathname
      });
    }
    setIsOpen(false);
    setPostId(null);
    setUsername(null);
    setShouldFocusComment(false);
    setTargetCommentId(null);
    setHighlightCommentId(null);
    setAutoExpandParentId(null);
  }, [location.pathname, location.search]);

  const openModal = useCallback((username: string, postId: string, action?: 'comment') => {
    // Type guard: Reject non-string usernames
    if (!username || typeof username !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PostModalContext] Invalid username provided to openModal:', {
          username,
          type: typeof username
        });
      }
      return;
    }
    
    // Capture origin route
    const currentPath = location.pathname;
    originRouteRef.current = currentPath;
    
    // If NOT on a profile route (e.g. /square), open in-place with query params
    if (!isProfileRoute(currentPath)) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PostModalContext] Non-profile origin, opening in-place:', { origin: currentPath });
      }
      isInPlaceModeRef.current = true;
      setIsInPlaceMode(true);
      setUsername(username);
      setPostId(postId);
      setIsOpen(true);
      setShouldFocusComment(action === 'comment');
      setTargetCommentId(null);
      setHighlightCommentId(null);
      setAutoExpandParentId(null);
      
      // Push history entry for back-button + deep-link
      const squareParams = new URLSearchParams();
      squareParams.set('modal', 'post');
      squareParams.set('post', postId);
      squareParams.set('user', username);
      if (action) squareParams.set('action', action);
      const squareUrl = `${currentPath}?${squareParams.toString()}`;
      navigate(squareUrl);
      urlParamsPushedRef.current = true;
      return;
    }
    
    // Profile route — use URL navigation as before
    isInPlaceModeRef.current = false;
    const url = `/profile/${username}/post/${postId}${action ? `?action=${action}` : ''}`;
    navigate(url);
  }, [navigate, location.pathname]);

  /**
   * Open modal in-place WITHOUT changing the URL
   * Used for notification clicks to keep user on current page
   */
  const openModalInPlace = useCallback((
    username: string, 
    postId: string, 
    options?: { commentId?: string; replyId?: string; parentCommentId?: string }
  ) => {
    // Type guard: Reject non-string usernames
    if (!username || typeof username !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PostModalContext] Invalid username provided to openModalInPlace:', {
          username,
          type: typeof username
        });
      }
      return;
    }
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PostModalContext] Opening modal in-place:', {
        username,
        postId,
        options
      });
    }
    
    // Set in-place mode to prevent URL monitoring from closing
    isInPlaceModeRef.current = true;
    urlParamsPushedRef.current = false;
    setIsInPlaceMode(true);
    
    // Set state immediately - no URL change
    setUsername(username);
    setPostId(postId);
    setIsOpen(true);
    setShouldFocusComment(false);
    
    // Set target comment for auto-scroll and highlight
    const commentToHighlight = options?.commentId || options?.replyId || null;
    setTargetCommentId(commentToHighlight);
    setHighlightCommentId(commentToHighlight);
    
    // Set parent comment ID for auto-expansion when navigating to a reply
    setAutoExpandParentId(options?.parentCommentId || null);
  }, []);

  const closeModal = useCallback((targetUsername?: string) => {
    // Set closing guard FIRST to prevent URL watcher from reopening during navigate
    isClosingRef.current = true;
    
    const origin = originRouteRef.current;
    const wasNonProfileOrigin = origin !== null && !isProfileRoute(origin);
    
    // Clear state
    setIsOpen(false);
    setPostId(null);
    setIsInPlaceMode(false);
    setShouldFocusComment(false);
    setTargetCommentId(null);
    setHighlightCommentId(null);
    setAutoExpandParentId(null);
    setUsername(null);
    isInPlaceModeRef.current = false;
    urlParamsPushedRef.current = false;
    originRouteRef.current = null;
    
    // If opened from a non-profile route (e.g. /square):
    // - No targetUsername → navigate back to remove modal query params
    // - With targetUsername → user explicitly wants to go to that profile
    if (wasNonProfileOrigin) {
      if (targetUsername && typeof targetUsername === 'string') {
        navigate(`/profile/${targetUsername}`);
      } else {
        // Navigate back to remove modal query params (history entry was pushed)
        navigate(origin || '/square', { replace: true });
      }
      queueMicrotask(() => {
        isClosingRef.current = false;
      });
      return;
    }
    
    // Profile origin — navigate to profile route as before
    const profileToNavigate = targetUsername || username;
    
    // Type guard: Only navigate with valid string username
    if (!profileToNavigate || typeof profileToNavigate !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PostModalContext] Invalid profileToNavigate:', {
          profileToNavigate,
          type: typeof profileToNavigate,
          targetUsername,
          username
        });
      }
      queueMicrotask(() => {
        isClosingRef.current = false;
      });
      return;
    }
    
    navigate(`/profile/${profileToNavigate}`, { replace: true });
    
    // Clear closing guard after navigate commits
    queueMicrotask(() => {
      isClosingRef.current = false;
    });
  }, [navigate, username]);

  /**
   * Close modal in-place WITHOUT navigation
   * Used when modal was opened via openModalInPlace
   */
  const closeModalInPlace = useCallback(() => {
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PostModalContext] Closing modal in-place (no navigation)');
    }
    
    // Set closing guard FIRST to prevent URL watcher from reopening
    isClosingRef.current = true;
    
    // Clear state without navigation
    setIsOpen(false);
    setPostId(null);
    setUsername(null);
    setIsInPlaceMode(false);
    setShouldFocusComment(false);
    setTargetCommentId(null);
    setHighlightCommentId(null);
    setAutoExpandParentId(null);
    
    // If URL has modal query params (pushed by openModal on /square),
    // strip them to prevent the URL watcher from reopening the modal.
    const currentSearch = new URLSearchParams(window.location.search);
    if (currentSearch.has('modal')) {
      const cleanPath = window.location.pathname;
      navigate(cleanPath, { replace: true });
    }
    
    // Clear refs AFTER navigate so URL watcher can't race
    // Use microtask to ensure navigate has committed
    queueMicrotask(() => {
      isInPlaceModeRef.current = false;
      urlParamsPushedRef.current = false;
      isClosingRef.current = false;
    });
  }, [navigate]);

  const clearFocusIntent = useCallback(() => {
    setShouldFocusComment(false);
  }, []);

  const clearHighlight = useCallback(() => {
    setHighlightCommentId(null);
  }, []);

  const value: PostModalContextType = {
    isOpen,
    postId,
    username,
    isInPlaceMode,
    shouldFocusComment,
    targetCommentId,
    highlightCommentId,
    autoExpandParentId,
    openModal,
    openModalInPlace,
    closeModal,
    closeModalInPlace,
    clearFocusIntent,
    clearHighlight
  };

  return (
    <PostModalContext.Provider value={value}>
      {children}
    </PostModalContext.Provider>
  );
};
